// PasskeysSection — 설정 > 보안 화면의 패스키 관리 섹션 (U66).
//
// 책임:
//   - 등록된 패스키 목록 표시 (라벨·등록일·마지막 사용 상대시간)
//   - 라벨 인라인 편집
//   - 패스키 삭제 (confirm + Undo 토스트 5초)
//   - 새 패스키 등록 dialog (deviceLabel 입력)
//   - WebAuthn 미지원 환경 fallback
//   - 빈 상태 표시
//
// 의존:
//   - usePasskeys / usePasskeyMutations (U65)
//   - detectCapabilities (U64 utils/webauthn)
//   - InlineEditable (기존)
//   - useToast (기존)
//   - useUndoContext (기존)
//   - WebauthnUserCancelledError / WebauthnNotSupportedError (U64)

import { useEffect, useState } from 'react';
import { usePasskeys, usePasskeyMutations } from '../hooks/usePasskeys';
import { detectCapabilities } from '../utils/webauthn';
import {
  WebauthnUserCancelledError,
  WebauthnNotSupportedError,
} from '../utils/webauthn';
import { InlineEditable } from './InlineEditable';
import { useToast } from '../components/Toast';
import { useUndoContext } from '../hooks/useUndoContext';
import type { PasskeySummary } from '../api/passkey';
import { Spinner } from './Spinner';
import { isApiError } from '../api/client';

// ─── 헬퍼: 상대 시간 포맷 ─────────────────────────────────────

/**
 * Unix timestamp(ms)를 한국어 상대 시간 문자열로 변환한다.
 * 예: "3분 전", "2일 전", "2026년 5월 3일"
 */
function relativeTime(tsMs: number): string {
  const diffMs = Date.now() - tsMs;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return '방금 전';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}일 전`;
  // 30일 이상이면 날짜 표시
  return new Date(tsMs).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ─── 헬퍼: WebAuthn 에러 → 한국어 메시지 ─────────────────────

/**
 * 등록 흐름에서 발생하는 에러를 한국어 메시지로 변환한다.
 */
function formatPasskeyError(err: unknown): string {
  if (err instanceof WebauthnNotSupportedError) {
    return '이 브라우저는 패스키를 지원하지 않습니다.';
  }
  if (err instanceof WebauthnUserCancelledError) {
    return '사용자가 취소했습니다.';
  }
  if (err instanceof Error) {
    // NotAllowedError 이름으로 오는 DOMException
    if (err.name === 'NotAllowedError') return '사용자가 취소했습니다.';
    if (err.name === 'AbortError') return '요청이 취소되었습니다.';
    if (err.name === 'SecurityError') return '보안 정책 오류입니다.';
    if (err.message) return err.message;
  }
  return '패스키 등록 중 오류가 발생했습니다.';
}

// ─── 기본 deviceLabel 생성 헬퍼 ──────────────────────────────

/**
 * 자동 생성 기기 이름.
 * navigator.platform(deprecated지만 여전히 널리 지원) + 오늘 날짜.
 */
function defaultDeviceLabel(): string {
  // navigator.platform은 deprecated이나 폴백으로 사용
  const platform =
    (typeof navigator !== 'undefined' && navigator.platform) || 'Device';
  const date = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${platform} — ${date}`;
}

// ─── RegisterDialog ────────────────────────────────────────────

interface RegisterDialogProps {
  /** 초기 라벨 (자동 생성값) */
  initialLabel: string;
  /** 등록 진행 중 여부 */
  isPending: boolean;
  /** 등록 실행 콜백 */
  onRegister: (label: string) => void;
  /** 취소 콜백 */
  onCancel: () => void;
}

/**
 * 패스키 등록 시 deviceLabel을 입력받는 인라인 dialog.
 * native <dialog>를 사용하지 않고 간단한 overlay로 구현한다.
 */
function RegisterDialog({
  initialLabel,
  isPending,
  onRegister,
  onCancel,
}: RegisterDialogProps) {
  const [label, setLabel] = useState(initialLabel);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;
    onRegister(trimmed);
  };

  return (
    // 배경 오버레이 — 클릭으로 닫기
    <div
      className="passkeys-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="패스키 등록"
      onClick={(e) => {
        // overlay 클릭(버블링)이면 닫기. dialog 내부 클릭은 stopPropagation 처리.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="passkeys-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h4>이 기기에 패스키 등록</h4>
        <p className="muted" style={{ marginBottom: 12 }}>
          기기 이름을 입력하세요. 나중에 목록에서 식별할 때 사용됩니다.
        </p>
        <form onSubmit={handleSubmit} className="stack" style={{ gap: 12 }}>
          <label>
            <span>기기 이름</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="예: iPhone 15 Pro, MacBook"
              autoFocus
              disabled={isPending}
              required
              style={{ marginTop: 4 }}
              aria-label="기기 이름"
            />
          </label>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={onCancel}
              disabled={isPending}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isPending || !label.trim()}
            >
              {isPending ? '등록 중…' : '등록'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── PasskeyRow ───────────────────────────────────────────────

interface PasskeyRowProps {
  passkey: PasskeySummary;
  onRename: (id: number, label: string) => Promise<void>;
  onDelete: (passkey: PasskeySummary) => void;
}

/**
 * 패스키 목록 한 행.
 * 라벨 인라인 편집, 등록일/마지막 사용 상대시간, 삭제 버튼.
 */
function PasskeyRow({ passkey, onRename, onDelete }: PasskeyRowProps) {
  const [editing, setEditing] = useState(false);

  return (
    <li className="passkey-row">
      {/* 라벨 영역 */}
      <div className="passkey-row__label">
        {editing ? (
          <InlineEditable
            value={passkey.deviceLabel}
            onSave={async (newLabel) => {
              await onRename(passkey.id, newLabel);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            autoFocus
            placeholder="기기 이름"
          />
        ) : (
          // 클릭하면 편집 모드 진입
          <button
            type="button"
            className="passkey-row__label-btn"
            onClick={() => setEditing(true)}
            title="클릭하여 이름 편집"
            aria-label={`${passkey.deviceLabel} — 이름 편집`}
          >
            {passkey.deviceLabel}
          </button>
        )}
      </div>

      {/* 메타 정보 */}
      <div className="passkey-row__meta muted">
        <span>등록: {relativeTime(passkey.createdAt)}</span>
        <span>
          마지막 사용:{' '}
          {passkey.lastUsedAt ? relativeTime(passkey.lastUsedAt) : '미사용'}
        </span>
      </div>

      {/* 삭제 버튼 */}
      <button
        type="button"
        className="btn btn--tiny danger"
        onClick={() => onDelete(passkey)}
        aria-label={`${passkey.deviceLabel} 삭제`}
      >
        ×
      </button>
    </li>
  );
}

// ─── PasskeysSection (메인) ────────────────────────────────────

/**
 * 설정 > 보안 화면에 삽입되는 패스키 관리 섹션.
 *
 * - WebAuthn 미지원 환경: 등록 버튼 disabled + 안내 메시지
 * - 등록된 패스키 없음: 빈 상태 메시지
 * - 등록/삭제/이름변경 흐름 제공
 */
export function PasskeysSection() {
  const toast = useToast();
  const undo = useUndoContext();

  const passkeysQ = usePasskeys();
  const { registerPasskey, renamePasskey, removePasskey } =
    usePasskeyMutations();

  // WebAuthn 지원 여부 (비동기 감지)
  const [webauthnAvailable, setWebauthnAvailable] = useState<boolean | null>(
    null,
  );

  // 등록 dialog 표시 여부
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);

  // WebAuthn 능력 감지 (마운트 시 1회)
  useEffect(() => {
    detectCapabilities()
      .then((caps) => {
        setWebauthnAvailable(caps.available);
      })
      .catch(() => {
        setWebauthnAvailable(false);
      });
  }, []);

  // ── 등록 핸들러 ─────────────────────────────────────────────

  const handleRegister = (label: string) => {
    registerPasskey.mutate(label, {
      onSuccess: () => {
        setShowRegisterDialog(false);
        toast.success(`패스키 "${label}"이(가) 등록되었습니다.`);
      },
      onError: (err) => {
        const msg = formatPasskeyError(err);
        toast.error(msg);
        // 사용자 취소(NotAllowedError)면 dialog 닫기
        if (
          err instanceof WebauthnUserCancelledError ||
          (err instanceof Error && err.name === 'NotAllowedError')
        ) {
          setShowRegisterDialog(false);
        }
      },
    });
  };

  // ── 이름변경 핸들러 ──────────────────────────────────────────

  const handleRename = async (id: number, label: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      renamePasskey.mutate(
        { id, label },
        {
          onSuccess: () => {
            toast.success('이름이 변경되었습니다.');
            resolve();
          },
          onError: (err) => {
            toast.error(
              err instanceof Error ? err.message : '이름 변경에 실패했습니다.',
            );
            reject(err);
          },
        },
      );
    });
  };

  // ── 삭제 핸들러 (confirm + Undo) ────────────────────────────

  const handleDelete = (passkey: PasskeySummary) => {
    if (!window.confirm(`"${passkey.deviceLabel}" 패스키를 삭제하시겠습니까?`)) {
      return;
    }

    removePasskey.mutate(passkey.id, {
      onSuccess: () => {
        // Undo 토스트 등록 (5초 안에 되돌리기)
        undo.push({
          description: `"${passkey.deviceLabel}" 패스키가 삭제되었습니다.`,
          duration: 5000,
          undo: async () => {
            // 패스키 재등록은 WebAuthn 흐름이 필요하므로 실제 되돌리기 불가.
            // 대신 사용자에게 재등록 안내 토스트.
            toast.info(
              '패스키는 서버에서 삭제된 후 되돌릴 수 없습니다. 다시 등록해 주세요.',
            );
          },
        });
      },
      onError: (err) => {
        toast.error(
          err instanceof Error ? err.message : '삭제에 실패했습니다.',
        );
      },
    });
  };

  // ── 렌더 ────────────────────────────────────────────────────

  // 능력 감지 중 로딩
  const capabilityLoading = webauthnAvailable === null;
  const unsupported = webauthnAvailable === false;

  // 서버에서 패스키가 꺼져 있음 (운영 env 미설정 → 503 passkey_disabled)
  const serverDisabled =
    isApiError(passkeysQ.error) && passkeysQ.error.error === 'passkey_disabled';

  if (serverDisabled) {
    return (
      <section className="card stack" aria-labelledby="passkeys-section-title">
        <h3 id="passkeys-section-title">패스키 (생체인식 로그인)</h3>
        <p className="muted">서버에서 패스키 기능이 꺼져 있습니다.</p>
      </section>
    );
  }

  return (
    <section className="card stack" aria-labelledby="passkeys-section-title">
      <h3 id="passkeys-section-title">패스키 (생체인식 로그인)</h3>
      <p className="muted">
        이 기기에 패스키를 등록하면 다음부터 지문·얼굴 인식으로 빠르게 로그인할
        수 있어요.
      </p>

      {/* 미지원 환경 경고 */}
      {unsupported && (
        <p className="muted" style={{ color: 'var(--warn, #b45309)' }}>
          이 브라우저는 패스키를 지원하지 않습니다.
        </p>
      )}

      {/* 목록 */}
      {passkeysQ.isLoading ? (
        <Spinner />
      ) : (
        <ul className="passkey-list" aria-label="등록된 패스키 목록">
          {(passkeysQ.data?.items ?? []).length === 0 ? (
            <li className="muted">등록된 패스키가 없습니다</li>
          ) : (
            (passkeysQ.data?.items ?? []).map((pk) => (
              <PasskeyRow
                key={pk.id}
                passkey={pk}
                onRename={handleRename}
                onDelete={handleDelete}
              />
            ))
          )}
        </ul>
      )}

      {/* 등록 버튼 */}
      <button
        type="button"
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        disabled={capabilityLoading || unsupported || registerPasskey.isPending}
        onClick={() => setShowRegisterDialog(true)}
        aria-disabled={unsupported}
      >
        {capabilityLoading ? '확인 중…' : '이 기기에 패스키 등록'}
      </button>

      {/* 등록 dialog */}
      {showRegisterDialog && (
        <RegisterDialog
          initialLabel={defaultDeviceLabel()}
          isPending={registerPasskey.isPending}
          onRegister={handleRegister}
          onCancel={() => {
            if (!registerPasskey.isPending) setShowRegisterDialog(false);
          }}
        />
      )}
    </section>
  );
}
